import { useState, useEffect, useMemo } from "react";
import { Card, Row, Col, Statistic, Spin } from "antd";
import {
  MessageOutlined,
  InboxOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  RobotOutlined,
  UserOutlined,
  TagOutlined,
  BranchesOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import {
  ConversationStatus,
  genericStyles,
  STATUS_COLORS,
} from "../../../constant/index";
import { DashboardContainer } from "./styles";
import httpClient from "../../../api/httpClient";
import { useSelector } from "react-redux";
import { selectSelectedTenantId } from "../../../store/user/selectors";

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const selectedTenantId = useSelector(selectSelectedTenantId);

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const { data } = await httpClient.get("insight", {
          params: { tenant_id: selectedTenantId },
        });
        const total =
          data?.conversation_stats?.reduce(
            (sum, item) => sum + item?.total,
            0
          ) || 0;

        setData({ ...data, total });
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (selectedTenantId) fetchDashboardData();
  }, [selectedTenantId]);

  const conversationStatuses = useMemo(() => {
    if (!data?.conversation_stats) return [];

    const statusMap = new Map(
      data.conversation_stats.map(({ status, total }) => [status, total])
    );

    return Object.values(ConversationStatus).map((status) => {
      const iconStyle = {
        color: STATUS_COLORS[status],
        fontSize: "18px",
      };

      let icon = null;
      switch (status) {
        case ConversationStatus.OPEN:
          icon = <MessageOutlined style={iconStyle} />;
          break;
        case ConversationStatus.PENDING:
          icon = <ClockCircleOutlined style={iconStyle} />;
          break;
        case ConversationStatus.RESOLVED:
          icon = <CheckCircleOutlined style={iconStyle} />;
          break;
        case ConversationStatus.ARCHIVED:
          icon = <InboxOutlined style={iconStyle} />;
          break;
        case ConversationStatus.BOT_HANDLING:
          icon = <RobotOutlined style={iconStyle} />;
          break;
        default:
          icon = null;
      }

      return {
        status,
        label: status.replace(/_/g, " "),
        total: statusMap.get(status) || 0,
        icon,
      };
    });
  }, [data?.conversation_stats]);

  return (
    <DashboardContainer>
      <div className="dashboard-title">Dashboard Overview</div>

      {loading ? (
        <div className="flex justify-center align-center w-100">
          <Spin size="large" tip="Loading dashboard..." />
        </div>
      ) : (
        <Row gutter={[16, 16]}>
          {/* Conversation Stats Card */}
          <Col xs={24} lg={12}>
            <Card
              title="Conversation Statistics"
              className="conversation-card"
              extra={<span>Total: {data?.total}</span>}
            >
              {conversationStatuses.map(({ status, label, total, icon }) => (
                <div key={status} className="conversation-item">
                  <div className="status-label">
                    <span className="status-icon">{icon}</span>
                    <span>{label}</span>
                  </div>
                  <div className="status-count">{total}</div>
                </div>
              ))}
            </Card>
          </Col>

          {/* Quick Stats */}
          <Col xs={24} lg={12}>
            <Row gutter={[16, 16]}>
              <Col xs={12}>
                <Card className="stat-card">
                  <Statistic
                    title="Users"
                    value={data.user_roles_count || "0"}
                    prefix={<UserOutlined style={{ color: "#1890ff" }} />}
                  />
                </Card>
              </Col>
              <Col xs={12}>
                <Card className="stat-card">
                  <Statistic
                    title="Topics"
                    value={data.topics_count || "0"}
                    prefix={
                      <TagOutlined
                        style={{ color: genericStyles.successColor }}
                      />
                    }
                  />
                </Card>
              </Col>
              <Col xs={12}>
                <Card className="stat-card">
                  <Statistic
                    title="Branches"
                    value={data.branches_count || "0"}
                    prefix={
                      <BranchesOutlined
                        style={{ color: genericStyles.processColor }}
                      />
                    }
                  />
                </Card>
              </Col>
              <Col xs={12}>
                <Card className="stat-card">
                  <Statistic
                    title="Teams"
                    value={data.teams_count || "0"}
                    prefix={
                      <TeamOutlined
                        style={{ color: genericStyles.primaryColor }}
                      />
                    }
                  />
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      )}
    </DashboardContainer>
  );
};

export default Dashboard;
